import { ArrowUp, CheckCircleLine, Delete, Download } from '@factorialco/f0-react/icons/app'
import { PaymentPageRoutesFragment } from 'generated/resources/graphql'

import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

import { statusLabelType, statusText } from 'modules/banking/lib/mappers'
import {
  shouldShowPayWithSepaTransfer,
  supportsSepaPayment,
} from 'modules/banking/lib/paymentHelpers'

export const buildPaymentHeaderComponentProps = (
  payment: PaymentPageRoutesFragment,
  canSeeSepaTransferAction: boolean
): ResourceHeadersPropsWithActions => {
  const lastPaymentExecution = payment.paymentExecutions[payment.paymentExecutions.length - 1]
  const totalTransfers = lastPaymentExecution?.transfersConnection.totalCount || 0
  const supportsSepa = supportsSepaPayment(
    payment.bank_account?.account?.providerConfiguration?.services
  )
  const showPayActions = shouldShowPayWithSepaTransfer(
    payment.status,
    supportsSepa,
    totalTransfers > 0
  )

  const seeSepaTransferAction = supportsSepa && canSeeSepaTransferAction

  // Primary action: Pay with Factorial (SEPA transfer)
  const primaryAction =
    seeSepaTransferAction && showPayActions
      ? {
          label: i18n.t('banking.payments.pay_with_sepa_transfer'),
          icon: ArrowUp,
          key: 'pay_with_sepa_transfer',
        }
      : undefined

  // Secondary actions
  const secondaryActions: Array<{
    label: string
    icon: typeof Download
    key: string
    critical?: boolean
  }> = []

  // Download SEPA file (when not using Swan/Factorial pay)
  if (!seeSepaTransferAction && lastPaymentExecution) {
    secondaryActions.push({
      label: i18n.t('banking.payments.download_sepa_file'),
      icon: Download,
      key: 'download_sepa_file',
    })
  }

  // Mark as paid (when payment is in progress and not using Swan)
  const paymentInProgress = payment.status === 'in_progress'
  if (!seeSepaTransferAction && paymentInProgress) {
    secondaryActions.push({
      label: i18n.t('banking.payments.mark_as_paid'),
      icon: CheckCircleLine,
      key: 'mark_as_paid',
    })
  }

  // Delete bulk payment (only if no executions or no transfers)
  const canDelete = !lastPaymentExecution || !totalTransfers
  if (canDelete) {
    secondaryActions.push({
      label: i18n.t('banking.payments.delete_bulk_payment'),
      icon: Delete,
      key: 'delete_bulk_payment',
      critical: true,
    })
  }

  return {
    title: payment.label ?? '',
    status: {
      label: '',
      text: statusText(payment.status),
      variant: statusLabelType(payment.status),
    },
    primaryAction,
    secondaryActions,
    metadata: [
      {
        label: i18n.t('banking.payments.detail_total_amount'),
        value: {
          type: 'text',
          content: formatCurrency(payment.amountCents, payment.currency),
        },
      },
      {
        label: i18n.t('banking.payments.detail_total_count'),
        value: {
          type: 'text',
          content: totalTransfers + '',
        },
      },
    ],
  }
}
